import { ProductSubscriptionCancelationReasonsEnum } from "dash-common";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalContext,
  RadioInput,
  Text,
  Textarea,
  ViewerContext,
  toast,
  useServerResponseTranslation,
} from "dash-ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import {
  GetCurrentBrokerDocument,
  SubscriptionProduct,
  useCancelProductSubscriptionMutation,
} from "gql/generated.graphql";
import { camelCase } from "lodash";
import { t } from "i18next";

const CancelSubscriptionModal = () => {
  const [otherReason, setOtherReason] = useState<string>();
  const { subscription: currentUserSubscription } = useContext(ViewerContext);
  const [selectedReason, selectReason] =
    useState<ProductSubscriptionCancelationReasonsEnum | null>();

  const theme = useTheme();
  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);
  const { t: billingPageLabels } = useTranslation("pages", {
    keyPrefix: "profilePage.billing",
  });
  const translateErrorMessage = useServerResponseTranslation();

  const [cancelProductSubscription, { loading }] =
    useCancelProductSubscriptionMutation({
      refetchQueries: [GetCurrentBrokerDocument],
    });

  const isOpen = isOpenCurrent["CancelSubscription"];

  const downgradeTo = payload?.downgradeTo as SubscriptionProduct | undefined;

  const isCancelation = downgradeTo?.name === "Basic";
  const isRecurringTypeChange =
    downgradeTo?.recurringType !==
      currentUserSubscription?.subscriptionProduct?.recurringType &&
    downgradeTo?.stripeProductId ===
      currentUserSubscription?.subscriptionProduct?.stripeProductId;

  // Translations
  const getModalTitle = () => {
    if (isCancelation) return billingPageLabels("cancelModalTitle");
    if (isRecurringTypeChange)
      return billingPageLabels("recurringTypeChangeModalTitle");
    return billingPageLabels("downgradeModalTitle");
  };

  const getModalText = () => {
    if (isCancelation) return billingPageLabels("cancelModalText");

    const recurringType =
      downgradeTo?.recurringType === "MONTH" ? "monthly" : "six-month";
    if (isRecurringTypeChange) {
      return billingPageLabels("recurringTypeChangeModalText", {
        plan: downgradeTo?.name,
        recurringType,
      });
    }

    return billingPageLabels("downgradeModalText", {
      plan: downgradeTo?.name,
      recurringType,
    });
  };

  const modalTitle = getModalTitle();
  const modalText = getModalText();

  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle || ""}
      onRequestClose={() => toggleModal(false, "CancelSubscription")}>
      <Box mt={1}>
        <Text color={theme.colors.n80}>{modalText}</Text>
      </Box>
      {isCancelation && (
        <Box mt={4}>
          {Object.values(ProductSubscriptionCancelationReasonsEnum).map(
            (reason) => (
              <Flex
                onClick={() => selectReason(reason)}
                alignItems="center"
                mb={4}
                key={reason}>
                <RadioInput
                  mr={2}
                  key={reason}
                  checked={reason === selectedReason}
                  onChange={() => selectReason(reason)}
                />
                <Text cursor="default">
                  {billingPageLabels(`cancelReasons.${reason}`)}
                </Text>
              </Flex>
            )
          )}
          {ProductSubscriptionCancelationReasonsEnum.OTHER ===
            selectedReason && (
            <Textarea
              value={otherReason}
              onChange={(event) => setOtherReason(event.target.value)}
              minHeight={80}
              placeholder={
                billingPageLabels("otherCancelReasonPlaceholder") || ""
              }
            />
          )}
        </Box>
      )}
      <Flex mt={4}>
        <Button
          mr={2}
          colorVariation="danger"
          disabled={isCancelation && !selectedReason}
          isLoading={loading}
          onClick={() =>
            cancelProductSubscription({
              variables: {
                input: {
                  cancelationReason:
                    selectedReason ||
                    (isRecurringTypeChange
                      ? "RECURRING_TYPE_CHANGE"
                      : "DOWNGRADE"),
                  downgradeTo: downgradeTo?.stripePriceId,
                  ...(otherReason && { otherReason }),
                },
              },
              onCompleted: () => {
                toggleModal(false, "CancelSubscription");
                toggleModal(false, "SubscriptionProductsDetails");
                toast.success(billingPageLabels("subscriptionProductChanged"));
                localStorage.removeItem("notificationDismissTimestamp");
              },
              onError: (error) => {
                toast.error(
                  translateErrorMessage(error, ["common:serverErrors"])
                );
              },
            })
          }>
          {isRecurringTypeChange
            ? billingPageLabels("changeRecurringType")
            : billingPageLabels("cancelPlan")}
        </Button>
        <Button
          variation="outline"
          onClick={() => {
            toggleModal(false, "CancelSubscription");
          }}>
          {billingPageLabels("backToBilling")}
        </Button>
      </Flex>
    </Modal>
  );
};

export default CancelSubscriptionModal;
